import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";
import { getMovieById } from "@/utils/movie";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const total = heroSlides.length;

  const next = useCallback(
    () => setIndex((i) => (i + 1) % total),
    [total]
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + total) % total),
    [total]
  );

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = heroSlides[index];
  const movie = getMovieById(slide.movieId);
  if (!movie) return null;

  return (
    <section className="relative w-full">
      <div className="relative h-[440px] w-full overflow-hidden md:h-[580px]">
        {heroSlides.map((s, i) => (
          <div
            key={s.movieId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={s.backdrop}
              alt={getMovieById(s.movieId)?.title ?? "秋霞网影视推荐"}
              title="秋霞网 影视推荐"
              className="h-full w-full object-cover object-center"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/50 to-transparent" />

        <div className="absolute inset-0 flex flex-col justify-end px-4 pb-10 md:justify-center md:px-12 md:pb-0">
          <div className="max-w-2xl">
            <h1 className="font-heading text-xs tracking-[0.3em] text-primary-400 md:text-sm">
              秋霞网 · 最新电影电视剧在线观看平台
            </h1>
            <h2 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:mt-4 md:text-5xl">
              {movie.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-400 md:mt-4 md:text-sm">
              <span className="rounded-md bg-primary-500 px-2 py-0.5 text-foreground-950">
                {movie.type === "movie" ? "电影" : "电视剧"}
              </span>
              <span className="flex items-center gap-1 font-medium text-accent-400">
                <i className="ri-star-fill text-xs" />
                {movie.rating.toFixed(1)}
              </span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.genres.join(" / ")}</span>
            </div>
            <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-foreground-500 md:mt-4 md:text-base">
              {slide.tagline}。{movie.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${movie.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to={`/detail/${movie.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-700/40 bg-background-50/60 px-6 py-3 text-sm font-medium text-foreground-900 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-400 cursor-pointer"
              >
                <i className="ri-information-line text-lg" />
                作品详情
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={prev}
          className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/60 text-foreground-900 backdrop-blur transition-colors hover:bg-primary-500 hover:text-foreground-950 md:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={next}
          className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-secondary-950/60 text-foreground-900 backdrop-blur transition-colors hover:bg-primary-500 hover:text-foreground-950 md:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-5 right-4 flex items-center gap-2 md:right-12">
          {heroSlides.map((s, i) => (
            <button
              key={s.movieId}
              type="button"
              aria-label={`查看第 ${i + 1} 张推荐`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index
                  ? "w-7 bg-primary-500"
                  : "w-3 bg-foreground-700/50 hover:bg-foreground-600"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}